import React from "react";
import type { DynamicToolUIPart } from "ai";
import { Text } from "@gusarov-studio/rubik-ui";
import { ToolCall, ToolCallPayload, type ToolCallStatus } from "./ToolCall";
import { formatToolPayload, getToolTimeSeconds, isMarkedInterrupted } from "../helpers/toolResults";
import { INTERRUPTED_TOOL_MESSAGE } from "../constants";

interface ToolCallLogProps {
  part: DynamicToolUIPart;
  isLiveStreaming: boolean;
}

/**
 * A call cut off before its tool returned: still waiting on it after the run
 * itself has ended, or a reloaded chat call stored as `interrupted` (which
 * comes back as a marked `output-error`). Shown apart from a real error, the
 * same way before and after a reload.
 */
function isInterrupted(part: DynamicToolUIPart, isLiveStreaming: boolean): boolean {
  if (part.state === "output-error") {
    return isMarkedInterrupted(part);
  }
  return (part.state === "input-streaming" || part.state === "input-available") && !isLiveStreaming;
}

function getStatus(part: DynamicToolUIPart, isLiveStreaming: boolean): ToolCallStatus {
  if (part.state === "output-available") {
    return "done";
  }
  if (part.state === "output-error" || part.state === "output-denied") {
    return "error";
  }
  return isInterrupted(part, isLiveStreaming) ? "error" : "running";
}

/**
 * One tool call in a run's output: the arguments the model sent and whatever the
 * sandboxed implementation returned or threw.
 */
const ToolCallLog: React.FC<ToolCallLogProps> = ({ part, isLiveStreaming }) => {
  "use memo";

  const status = getStatus(part, isLiveStreaming);
  const interrupted = isInterrupted(part, isLiveStreaming);
  const durationSeconds = getToolTimeSeconds(part);
  const input = formatToolPayload(part.input);
  const output = part.state === "output-available" ? formatToolPayload(part.output) : "";

  return (
    <ToolCall
      name={part.toolName}
      status={status}
      durationSeconds={durationSeconds}
    >
      {input && (
        <ToolCallPayload
          label="Input"
          value={input}
        />
      )}

      {part.state === "output-available" && (
        <ToolCallPayload
          label="Output"
          value={output || "(empty)"}
        />
      )}

      {part.state === "output-error" && !interrupted && (
        <ToolCallPayload
          label="Error"
          variant="danger"
          value={part.errorText}
        />
      )}

      {interrupted && (
        <Text
          size="12"
          color="muted"
        >
          {INTERRUPTED_TOOL_MESSAGE}
        </Text>
      )}
    </ToolCall>
  );
};

export { ToolCallLog };
